import { useTranslation } from 'react-i18next';
import { passwordClueToPhrase, type Mark, type PasswordClue } from '@il-colpo/engine';
import { Button } from '../../ui/index.ts';
import { useGame } from '../GameContext.tsx';
import { Bystander, ClueLine, HeardList, type PuzzleProps } from './shared.tsx';
import styles from './puzzles.module.css';

interface PasswordView {
  length: number;
  clueList?: PasswordClue[];
  candidates?: string[];
  guesses?: { word: number; marks: Mark[] }[];
  attemptsLeft?: number;
  lockedUntilMs?: number;
}

const HEARD = ['passClue', 'passTry'] as const;
/** Marks also differ by shape, so colour is never the only cue. */
const MARK_GLYPH: Record<Mark, string> = { hit: '●', present: '◐', miss: '○' };

/** Password (GDD §8.6): one of the candidate words; the others hold the clues. */
export function PasswordPanel({ puzzleId, type, v: raw }: PuzzleProps) {
  const { t } = useTranslation('game');
  const { role, act, view } = useGame();
  const v = raw as unknown as PasswordView;

  if (role === 'hacker' && v.candidates) {
    const tried = new Set((v.guesses ?? []).map((g) => g.word));
    const locked = (v.lockedUntilMs ?? 0) > (view.exec?.elapsedMs ?? 0);
    return (
      <div className={styles['stack']}>
        <p className={styles['progress']}>
          {t('puzzle.password.left', { count: v.attemptsLeft ?? 0 })}
        </p>
        <div className={styles['terminal']}>
          <ul className={styles['words']}>
            {v.candidates.map((w, i) => {
              const guess = v.guesses?.find((g) => g.word === i);
              return (
                <li key={w}>
                  <Button
                    size="md"
                    variant="secondary"
                    disabled={tried.has(i) || locked}
                    onClick={() => act(puzzleId, { kind: 'password.try', word: i })}
                  >
                    <span className={styles['word']}>
                      {guess
                        ? [...w].map((ch, k) => (
                            <span
                              key={k}
                              className={[styles['letter'], styles[guess.marks[k] ?? 'miss']].join(
                                ' ',
                              )}
                            >
                              {ch}
                              <span className={styles['mark']} aria-hidden="true">
                                {MARK_GLYPH[guess.marks[k] ?? 'miss']}
                              </span>
                            </span>
                          ))
                        : w}
                    </span>
                  </Button>
                </li>
              );
            })}
          </ul>
        </div>
        {locked ? <p className={styles['status']}>{t('puzzle.password.locked')}</p> : null}
        <HeardList puzzleId={puzzleId} ids={HEARD} />
      </div>
    );
  }

  const clues = v.clueList ?? [];
  if (clues.length > 0) {
    return (
      <div className={styles['stack']}>
        <p className={styles['muted']}>{t('puzzle.password.clueHint')}</p>
        <ul className={styles['clues']}>
          {clues.map((c, i) => (
            <ClueLine key={i} phrase={passwordClueToPhrase(c)} />
          ))}
        </ul>
      </div>
    );
  }
  return <Bystander type={type} />;
}
